// Why a camera would not open, and whether it sent anything.
//
// Two pure functions, kept out of age.js so they can be tested without a
// browser (age.js reaches config.js, which only exists under Vite). Both came
// out of one report: "the age check isn't working, the camera is not being
// accessed and it just keeps saying try again" -- undiagnosable, because every
// camera failure was answered with the same "allow the camera" line and the
// browser's own error was discarded. ASCII only.

/**
 * What a camera failure actually was, and what to do about it. The error name
 * rides along at the end so a screenshot says which of these happened.
 * @param {any} err  the DOMException getUserMedia rejected with
 */
export function cameraErrorMessage(err) {
  const name = (err && err.name) || 'Error';
  const tail = ` (${name})`;
  switch (name) {
    case 'NotAllowedError':
    case 'SecurityError':
      return `Lumi needs camera access to verify your age. Tap the lock icon next to the address bar, allow the camera, then try again.${tail}`;
    case 'NotReadableError':
    case 'AbortError':
      return `Your camera is already in use by something else, such as another Lumi tab, a video call, or another app. Close it and try again.${tail}`;
    case 'NotFoundError':
    case 'DevicesNotFoundError':
      return `No camera was found on this device. Lumi needs one to check your age.${tail}`;
    case 'OverconstrainedError':
      return `Your camera could not be opened at the size Lumi asked for. Please try again.${tail}`;
    default:
      return `Lumi could not open your camera${tail}. Close anything else using it, then try again.`;
  }
}

/**
 * How much a frame VARIES (luma variance).
 *
 * A camera that granted a stream but never delivered a frame draws as one flat
 * colour, and a flat 4K JPEG is still tens of kilobytes -- so a size filter
 * passes it, the estimator answers "no face", and the person is told to try
 * again as though their face had been rejected. Near-zero variance is a lens
 * cap, a stream that never started, or a camera another tab is holding.
 * @param {Uint8ClampedArray|number[]} rgba  RGBA pixels
 */
export function lumaVariance(rgba) {
  const n = Math.floor(rgba.length / 4);
  if (!n) return 0;
  let sum = 0;
  let sumSq = 0;
  for (let i = 0; i < rgba.length; i += 4) {
    const luma = 0.299 * rgba[i] + 0.587 * rgba[i + 1] + 0.114 * rgba[i + 2];
    sum += luma;
    sumSq += luma * luma;
  }
  const mean = sum / n;
  return Math.max(0, sumSq / n - mean * mean);
}

/** Below this a frame carries nothing a face detector could use. */
export const BLANK_VARIANCE = 12;
